/*
  Everything that is true only inside the iOS app.

  The same bundle serves the website and the app. `isNativeApp()` is how a screen asks which
  one it is running in, and on the web it is a constant false that lets the rest of the
  branch fall out of the build.

  ── The rule that shapes this file ──────────────────────────────────────────

  App Store Review Guideline 3.1.3(f) lets a free app act as a companion to a paid web tool
  without handing Apple a cut, on one condition: no purchasing inside the app, and no calls
  to action to purchase outside it. At $249 a month that condition is worth $37.35 per
  subscriber per month under the Small Business Program, and $74.70 above it.

  So inside the app there is no price, no Stripe, no "subscribe", no link to the website, and
  no wording that points at any of them. An account that has lapsed is told it is not active
  and given a way to sign out. That is the whole conversation. `Billing.tsx` is where this is
  enforced; this file is where it is explained.

  Apple relaxed the anti-steering rules on the US storefront in May 2025 after the Epic
  injunction, so a US-only app could link out. The App Store is worldwide by default and the
  relaxation is not, so nothing here relies on it.
*/

import { Capacitor } from '@capacitor/core'
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'

/** True in the iOS app, false in every browser. */
export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform()
}

/** 'ios', 'android' or 'web'. */
export function nativePlatform(): string {
  return Capacitor.getPlatform()
}

/**
 * What a tap on the camera produced.
 *
 * Three outcomes rather than a File or null, because null was hiding the only two states
 * worth telling apart. Closing the shutter is the owner's own decision and says nothing. A
 * camera that is not there, or not allowed, is a fault, and swallowing it looks to the owner
 * exactly like a button that does nothing.
 */
export type Capture =
  | { photo: File }
  | { cancelled: true }
  | { error: string }

/**
 * Opens the camera and hands back the photograph as a jpeg File.
 *
 * This is the reason the app is an app rather than a bookmark. Owners' numbers arrive as a
 * photograph of a statement taken on a phone in a van, and `<input type="file">` on iOS puts
 * two taps and a sheet between them and the shutter.
 *
 * `CameraSource.Camera` rather than Prompt: the paperclip beside this button already covers
 * the library, so a second sheet asking camera-or-library would be asking a question the
 * user has already answered by pressing the one with the camera on it.
 *
 * Quality 82 and 2000px because the far side of this is a billed vision call
 * (`ImageDocumentReader`) that reads figures off the page. Below about 1600px the small type
 * on a bank statement stops being legible to it; above 2000 nothing improves and the upload
 * takes longer on a job site.
 *
 * ── Base64 rather than Uri ──────────────────────────────────────────────────
 *
 * `CameraResultType.Uri` hands back a `capacitor://localhost/_capacitor_file_/...` path that
 * only becomes a file by being fetched. `CapacitorHttp` is enabled in `capacitor.config.ts`
 * and is load-bearing for the session cookie, and enabling it replaces `window.fetch` with
 * one that goes out through the native URLSession. That patched fetch is an HTTP client: a
 * local file URL through it is not a request it can make. Reading the bytes off the result
 * directly needs no fetch at all, which is the only way these two features coexist.
 */
export async function capturePhoto(): Promise<Capture> {
  if (!isNativeApp()) return { error: 'The camera is only available in the Boardium app.' }

  try {
    const photo = await Camera.getPhoto({
      source: CameraSource.Camera,
      resultType: CameraResultType.Base64,
      quality: 82,
      width: 2000,
      correctOrientation: true,
    })

    /*
      The plugin hands back jpeg on iOS, which is the only reason this works at all: the
      Anthropic API accepts jpeg, png, gif and webp and nothing else, and the raw capture on
      an iPhone is HEIC. An HEIC reaching the upload endpoint is told to convert, and being
      told that about a photograph the app itself just took would be absurd.
    */

    if (!photo.base64String) return { error: 'That photograph came back empty. Try it again.' }

    const bytes = Uint8Array.from(atob(photo.base64String), c => c.charCodeAt(0))
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
    return { photo: new File([bytes], `photo-${stamp}.jpg`, { type: 'image/jpeg' }) }
  } catch (e) {
    return Reason(e)
  }
}

/**
 * What to make of a thrown camera.
 *
 * Capacitor reports a cancel the same way it reports a fault, as an Error whose message reads
 * like something went wrong, so the message is the only thing there is to sort them by.
 * Everything that is not a cancel is said out loud rather than swallowed, because the whole
 * defect this replaces was a silent one.
 */
function Reason(e: unknown): Capture {
  const message = e instanceof Error ? e.message : String(e ?? '')
  const code = (e as { code?: string } | null)?.code

  if (/cancel/i.test(message) || /no image picked/i.test(message)) return { cancelled: true }

  if (/denied|permission|not authorized/i.test(message))
    return { error: 'Boardium does not have permission to use the camera. Settings, then Boardium, then Camera.' }

  // The plugin is in package.json but not in the built app: `npm run app:sync` was not run
  // after it was added, so the JavaScript call reaches a bridge with nothing behind it.
  if (code === 'UNIMPLEMENTED' || /not implemented|unimplemented/i.test(message))
    return { error: 'The camera is missing from this build of the app.' }

  if (/unavailable|not available|simulator/i.test(message))
    return { error: 'This device has no camera available.' }

  return { error: message || 'The camera could not be opened.' }
}

/**
 * Native chrome, once, at boot. A no-op in every browser.
 *
 * The status bar is the only one of these that is a design decision rather than plumbing.
 * The app's top bar is the page colour (`MobileChrome` in Shell.tsx) and runs under the
 * clock, so the clock and the battery have to be dark to read against it.
 *
 * `Style.Light` means dark content for a light background, which is the opposite of what
 * the name suggests and the opposite of what the comment here used to claim. The claim was
 * wrong and the code was right, which is the worst way round: while the bar was navy the
 * clock, the signal bars and the battery rendered near-black on it and measured 1.33:1,
 * and anyone fixing the "bug" by trusting the comment would have flipped the one line that
 * was correct. Measured off docs/current-look-dashboard.PNG. On the light bar the same
 * setting measures 19.59:1.
 *
 * The splash is dismissed here rather than left on its own timer, because the timer fires
 * whether or not React has painted and the gap shows as a white flash between the launch
 * image and the app.
 */
export async function initNative(): Promise<void> {
  if (!isNativeApp()) return

  /*
    A hook for the handful of places where the app and the website want different geometry
    rather than different behaviour, which CSS can then key off without importing anything.

    So far that is the tab bar. In the app the only thing under it is the home indicator,
    and env(safe-area-inset-bottom) already reserves that, so the bar can sit close to the
    bottom edge. In a browser the thing under it is Safari's own address bar, which is a
    second bar the page cannot see and cannot move, so the two need air between them or
    they read as one confused strip.
  */
  document.documentElement.classList.add('is-native')

  const [{ StatusBar, Style }, { SplashScreen }, { Keyboard, KeyboardResize }] =
    await Promise.all([
      import('@capacitor/status-bar'),
      import('@capacitor/splash-screen'),
      import('@capacitor/keyboard'),
    ])

  try {
    await StatusBar.setStyle({ style: Style.Light })
  } catch { /* no status bar to style; not worth a broken boot */ }

  try {
    // Native resize rather than the default body resize: the shell is a fixed top bar over a
    // scrolling main, and resizing the body under it leaves the bar floating mid-screen
    // while the keyboard is up.
    await Keyboard.setResizeMode({ mode: KeyboardResize.Native })
  } catch { /* older iOS, or the plugin is absent */ }

  try {
    await SplashScreen.hide()
  } catch { /* already gone */ }
}
